/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CaretRight, FileText, Plus, X, FloppyDisk } from "@phosphor-icons/react";

/**
 * Collapsible "Report details" panel (Notes area). One place for the solicitor
 * to fill the merge-field values that report letters (Mortgage Report, Report
 * on Title, etc.) pull from. Values are stored letter-ready on the case
 * (`case.merge_fields`) and pre-fill any report using that {{tag}}.
 */
const GROUPS = [
  {
    key: "mortgage",
    title: "Mortgage",
    fields: [
      { tag: "lender", label: "Lender", type: "text" },
      { tag: "mortgage_type", label: "Mortgage type", type: "text" },
      { tag: "mortgage_term", label: "Term", type: "text" },
      { tag: "mortgage_advance", label: "Advance", type: "money" },
      { tag: "mortgage_cashback", label: "Cashback", type: "money" },
      { tag: "tt_fee", label: "Telegraphic transfer fee", type: "money" },
      { tag: "interest_rate", label: "Interest rate", type: "text" },
      { tag: "fixed_date", label: "Rate fixed until", type: "date" },
      { tag: "erc_date", label: "Early repayment charge until", type: "date" },
      { tag: "acting_lender", label: "Lender conveyancing fee", type: "money" },
    ],
  },
  {
    key: "leasehold",
    title: "Leasehold",
    fields: [
      { tag: "lease_date", label: "Date of lease", type: "date" },
      { tag: "lease_commence", label: "Lease commencement", type: "date" },
      { tag: "lease_term", label: "Lease term (years)", type: "text" },
      { tag: "ground_rent", label: "Ground rent", type: "text" },
      { tag: "service_amount", label: "Service charge amount (per annum)", type: "money" },
      { tag: "retention_amount", label: "Retention amount", type: "money" },
      { tag: "works_yes_no", label: "Major works planned (is / is not)", type: "text" },
      { tag: "custom_info", label: "Highlighted points for the client", type: "text" },
    ],
  },
  {
    key: "management",
    title: "Management",
    fields: [
      { tag: "freeholder_name", label: "Freeholder / landlord", type: "text" },
      { tag: "management_company", label: "Management company", type: "text" },
      { tag: "custom_postcompmng", label: "Post-completion management fee (per company/landlord)", type: "text" },
    ],
  },
  {
    key: "leasehold_postcompletion",
    title: "Post-completion notices (leasehold)",
    fields: [
      { tag: "noticet_feemng", label: "Notice of transfer — management co (fee)", type: "text" },
      { tag: "noticet_UKC", label: "Notice of transfer — UKC action", type: "text" },
      { tag: "notice_mng", label: "Notice of charge — management co", type: "text" },
      { tag: "notice_UKC", label: "Notice of charge — UKC action", type: "text" },
      { tag: "doc_mng", label: "Deed of covenant — management co", type: "text" },
      { tag: "doc_UKC", label: "Deed of covenant — UKC action", type: "text" },
      { tag: "member_mng", label: "Membership — management co", type: "text" },
      { tag: "member_UKC", label: "Membership — UKC action", type: "text" },
      { tag: "certofcomp_mng", label: "Certificate of compliance — management co", type: "text" },
      { tag: "certofcomp_UKC", label: "Certificate of compliance — UKC action", type: "text" },
      { tag: "noticet_feeLL", label: "Notice of transfer — landlord (fee)", type: "text" },
      { tag: "noticet_feeUKC2", label: "Notice of transfer — UKC action (landlord)", type: "text" },
      { tag: "notice_LL", label: "Notice of charge — landlord", type: "text" },
      { tag: "notice_UKC2", label: "Notice of charge — UKC action (landlord)", type: "text" },
      { tag: "doc_LL", label: "Deed of covenant — landlord", type: "text" },
      { tag: "doc_UKC2", label: "Deed of covenant — UKC action (landlord)", type: "text" },
      { tag: "member_LL", label: "Membership — landlord", type: "text" },
      { tag: "member_UKC2", label: "Membership — UKC action (landlord)", type: "text" },
      { tag: "certofcomp_LL", label: "Certificate of compliance — landlord", type: "text" },
      { tag: "certofcomp_UKC2", label: "Certificate of compliance — UKC action (landlord)", type: "text" },
    ],
  },
];

const KNOWN_TAGS = new Set(GROUPS.flatMap((g) => g.fields.map((f) => f.tag)));

const normalizeMoney = (v) => {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  const cleaned = s.replace(/[£,\s]/g, "");
  if (/^-?\d+(\.\d+)?$/.test(cleaned)) {
    return `£${Number(cleaned).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return s; // free text e.g. "£250 per annum"
};
const longToISO = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};
const isoToLong = (v) => {
  if (!v) return "";
  const d = new Date(`${v}T00:00:00`);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

export default function ReportDetailsPanel({ caseData, isSolicitor, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [customFields, setCustomFields] = useState([]); // [{tag, label, value}]
  const [newLabel, setNewLabel] = useState("");
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [registerPrefill, setRegisterPrefill] = useState({});
  const [fromRegister, setFromRegister] = useState({});

  const stored = useMemo(() => caseData?.merge_fields || {}, [caseData]);
  const storedLabels = useMemo(() => caseData?.merge_field_labels || {}, [caseData]);

  useEffect(() => {
    if (!caseData?.id) return;
    API.get(`/cases/${caseData.id}/register-prefill`)
      .then(({ data }) => setRegisterPrefill(data?.fields || {}))
      .catch(() => setRegisterPrefill({}));
  }, [caseData?.id]);

  useEffect(() => {
    const v = {};
    const reg = {};
    GROUPS.forEach((g) => g.fields.forEach((f) => {
      const manual = stored[f.tag];
      let raw = "";
      if (manual != null && manual !== "") {
        raw = manual;
      } else if (registerPrefill[f.tag]) {
        raw = registerPrefill[f.tag];
        reg[f.tag] = true;
      }
      v[f.tag] = f.type === "date" ? longToISO(raw) : raw;
    }));
    setValues(v);
    setFromRegister(reg);
    setCustomFields(
      Object.entries(stored)
        .filter(([tag]) => !KNOWN_TAGS.has(tag))
        .map(([tag, value]) => ({ tag, label: storedLabels[tag] || "", value })),
    );
  }, [stored, storedLabels, registerPrefill]);

  if (!caseData || !isSolicitor) return null;

  const filledCount = Object.values(stored).filter((x) => x !== "" && x != null).length;

  const setVal = (tag, val) => {
    setValues((s) => ({ ...s, [tag]: val }));
    setFromRegister((r) => (r[tag] ? { ...r, [tag]: false } : r));
  };

  const slugify = (s) =>
    (s || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^([0-9])/, "_$1");

  const addCustom = () => {
    const label = newLabel.trim();
    const t = (newTag.trim() || slugify(label));
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(t)) {
      toast.error("Enter a description (or a valid merge-tag name)");
      return;
    }
    if (KNOWN_TAGS.has(t) || customFields.some((c) => c.tag === t)) {
      toast.error("That field already exists");
      return;
    }
    setCustomFields((c) => [...c, { tag: t, label, value: "" }]);
    setNewLabel("");
    setNewTag("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fields = {};
      const labels = {};
      GROUPS.forEach((g) => g.fields.forEach((f) => {
        const raw = values[f.tag] ?? "";
        fields[f.tag] = f.type === "money" ? normalizeMoney(raw) : f.type === "date" ? isoToLong(raw) : String(raw).trim();
      }));
      customFields.forEach((c) => {
        fields[c.tag] = String(c.value ?? "").trim();
        labels[c.tag] = String(c.label ?? "").trim();
      });
      const { data } = await API.put(`/cases/${caseData.id}/merge-fields`, { fields, labels });
      toast.success("Report details saved");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm mt-4" data-testid="report-details-panel">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-slate-50 rounded-lg transition-colors"
            data-testid="report-details-toggle"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              <FileText weight="bold" className="w-4 h-4 text-slate-500" />
              Report details
              <span className="text-[11px] font-normal text-slate-400">
                — fill once to auto-fill reports{filledCount ? ` · ${filledCount} saved` : ""}
              </span>
            </span>
            <CaretRight weight="bold" className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-5 pb-5 pt-1 space-y-5 border-t border-slate-100">
            {GROUPS.map((g) => (
              <div key={g.key} data-testid={`report-group-${g.key}`}>
                <h5 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-3">{g.title}</h5>
                <div className="grid grid-cols-2 gap-3">
                  {g.fields.map((f) => (
                    <div key={f.tag} className="space-y-1">
                      <Label className="text-[11px] font-medium text-slate-600 flex items-center gap-1.5">
                        {f.label}
                        {fromRegister[f.tag] && (
                          <span title="Auto-filled from the HMLR title register — please verify" className="inline-flex items-center text-[8px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 rounded px-1 py-px" data-testid={`report-hmlr-badge-${f.tag}`}>
                            HMLR
                          </span>
                        )}
                      </Label>
                      {f.type === "money" ? (
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none">£</span>
                          <Input value={values[f.tag] ?? ""} onChange={(e) => setVal(f.tag, e.target.value)} placeholder="0.00" className="pl-6 h-8 text-xs" data-testid={`report-field-${f.tag}`} />
                        </div>
                      ) : f.type === "date" ? (
                        <Input type="date" value={values[f.tag] ?? ""} onChange={(e) => setVal(f.tag, e.target.value)} className="h-8 text-xs" data-testid={`report-field-${f.tag}`} />
                      ) : (
                        <Input value={values[f.tag] ?? ""} onChange={(e) => setVal(f.tag, e.target.value)} className="h-8 text-xs" data-testid={`report-field-${f.tag}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Custom fields */}
            <div data-testid="report-group-custom">
              <h5 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-3">Custom fields</h5>
              {customFields.length > 0 && (
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {customFields.map((c, i) => (
                    <div key={c.tag} className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Input
                          value={c.label ?? ""}
                          onChange={(e) => setCustomFields((cf) => cf.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                          placeholder="Field description"
                          className="h-6 text-[11px] font-medium border-0 border-b border-slate-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-slate-400"
                          data-testid={`report-custom-label-${c.tag}`}
                        />
                        <button onClick={() => setCustomFields((cf) => cf.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-500 shrink-0" data-testid={`report-custom-remove-${c.tag}`}>
                          <X weight="bold" className="w-3 h-3" />
                        </button>
                      </div>
                      <Input value={c.value ?? ""} onChange={(e) => setCustomFields((cf) => cf.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} className="h-8 text-xs" data-testid={`report-custom-field-${c.tag}`} />
                      <p className="text-[9px] text-slate-300 font-mono">{`{{${c.tag}}}`}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Field description (e.g. Reserve fund balance)" className="h-8 text-xs flex-1" data-testid="report-custom-new-label" />
                <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())} placeholder="merge tag (auto)" className="h-8 text-xs w-40 font-mono" data-testid="report-custom-new-tag" />
                <Button type="button" variant="outline" size="sm" onClick={addCustom} className="h-8 gap-1 text-xs" data-testid="report-custom-add-btn">
                  <Plus weight="bold" className="w-3.5 h-3.5" /> Add field
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">The merge tag must match the <span className="font-mono">{`{{tag}}`}</span> in the report; leave it blank to auto-generate from the description.</p>
            </div>

            <div className="flex justify-end pt-1">
              <Button onClick={handleSave} disabled={saving} className="text-white gap-1.5" style={{ background: "#100f5d" }} data-testid="report-details-save-btn">
                <FloppyDisk weight="bold" className="w-4 h-4" />
                {saving ? "Saving…" : "Save report details"}
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
